// Typed client for POST /api/chat. Mirrors the server response shape
// from packages/api/src/routes/chat.ts.

import { useCallback } from 'react';
import { type AuthedFetch, useAuthedFetch } from '../hooks/useAuthedFetch';

export interface ChatReply {
  messageId: string | null;
  conversationId: string | null;
  reply: string;
  escalation: { triggered: boolean; reasons: string[] };
  finishReason: string;
  persisted: boolean;
}

interface ApiSuccess<T> {
  ok: true;
  data: T;
}
interface ApiError {
  ok: false;
  error: { code: string; message: string };
}

export async function postChat(
  authedFetch: AuthedFetch,
  message: string,
  conversationId?: string,
): Promise<ChatReply> {
  const res = await authedFetch('/api/chat', {
    method: 'POST',
    body: JSON.stringify({ message, ...(conversationId ? { conversationId } : {}) }),
  });
  const body = (await res.json()) as ApiSuccess<ChatReply> | ApiError;
  if (!body.ok) throw new Error(`${body.error.code}: ${body.error.message}`);
  return body.data;
}

export function usePostChat(): (message: string, conversationId?: string) => Promise<ChatReply> {
  const authedFetch = useAuthedFetch();
  return useCallback(
    (message, conversationId) => postChat(authedFetch, message, conversationId),
    [authedFetch],
  );
}
